<template>
  <div class="organization-list">
    <el-row :gutter="20" class="operate-bar">
      <el-col :span="8" align="left">
        <!-- 新增组织机构按钮 -->
        <el-button icon="el-icon-plus" type="primary" @click="openAddDialog"
          v-hasPermission="'org:edit'">新增组织机构</el-button>
        <!-- 批量删除按钮（选中组织机构后可用） -->
        <el-button icon="el-icon-delete" type="danger" v-hasPermission="'org:edit'" @click="handleBatchDelete"
          :disabled="!this.hasPermission('org:edit') || selectedIds.length === 0">批量删除</el-button>
      </el-col>
      <!-- 搜索区域 -->
      <el-col :span="16" align="right">
        <el-form :inline="true" :model="searchForm" class="search-form">
          <el-form-item label="组织机构名称">
            <el-input v-model="searchForm.orgName" placeholder="请输入组织机构名称" maxlength="20"></el-input>
          </el-form-item>
          <el-form-item label="联系人">
            <el-input v-model="searchForm.leader" placeholder="请输入联系人" maxlength="20"></el-input>
          </el-form-item>
          <el-form-item label="关键词">
            <el-input v-model="searchForm.keyword" placeholder="请输入关键词" maxlength="20"></el-input>
          </el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="loadOrganizations">查询</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </el-form>
      </el-col>
    </el-row>

    <!-- 数据表格 -->
    <el-table :data="organizations" border stripe style="width: 100%" @selection-change="handleSelectionChange">
      <el-table-column type="selection" width="55" align="center"></el-table-column>
      <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
      <el-table-column prop="orgName" label="组织机构名称" header-align="center"></el-table-column>
      <el-table-column prop="subMchId" label="是否绑定微信子商户" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.subMchId ? 'success' : 'danger'">
            {{ scope.row.subMchId ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="location" label="所在地" align="center"></el-table-column>
      <el-table-column prop="leader" label="联系人" align="center"></el-table-column>
      <el-table-column prop="contact" label="联系方式" header-align="center"></el-table-column>
      <el-table-column prop="orgAddress" label="详细地址" header-align="center"></el-table-column>
      <el-table-column prop="remark" label="备注" header-align="center"></el-table-column>
      <el-table-column prop="createTime" label="创建时间"
        :formatter="(row, column, cellValue) => $options.filters.formatTime(cellValue)"
        align="center"></el-table-column>
      <el-table-column label="操作" align="center">
        <template slot-scope="scope">
          <el-tooltip content="编辑" placement="top">
            <el-button icon="el-icon-edit" type="primary" circle v-hasPermission="'org:edit'"
              @click="openEditDialog(scope.row)"></el-button>
          </el-tooltip>
          <el-tooltip content="删除" placement="top">
            <el-button icon="el-icon-delete" type="danger" circle v-hasPermission="'org:edit'"
              @click="handleDelete(scope.row.id)"></el-button>
          </el-tooltip>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange" :current-page="currentPage"
      :page-sizes="[10, 20, 50]" :page-size="pageSize" layout="total, sizes, prev, pager, next, jumper"
      :total="total"></el-pagination>

    <!-- 新增/编辑对话框 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px">
      <el-form :model="form" :rules="rules" ref="form" label-width="150px">
        <el-form-item label="组织机构名称" prop="orgName">
          <el-input v-model="form.orgName" maxlength="30"></el-input>
        </el-form-item>
        <el-form-item label="所在地" prop="location">
          <el-input v-model="form.location" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="联系人" prop="leader">
          <el-input v-model="form.leader" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="联系方式" prop="contact">
          <el-input v-model="form.contact" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="详细地址" prop="orgAddress">
          <el-input v-model="form.orgAddress" maxlength="40"></el-input>
        </el-form-item>
        <el-form-item label="备注" prop="remark">
          <el-input type="textarea" v-model="form.remark" rows="3" maxlength="200"></el-input>
        </el-form-item>
        <el-form-item label="微信子商户" prop="subMchId">
          <el-select v-model="form.subMchId" filterable placeholder="请选择微信子商户" clearable style="width: 100%">
            <el-option v-for="subMerchant in subMerchants" :key="subMerchant.subMchId" :label="subMerchant.subMchName + '(' + subMerchant.subMchId + ')'" :value="subMerchant.subMchId"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false; $refs.form.resetFields()">取消</el-button>
        <el-button type="primary" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import orgApi from '../../api/organization'
import subMerchantApi from '../../api/subMerchant';
import { hasPermission } from '../../utils/hasPermission'

export default {
  name: 'OrganizationList',
  data() {
    return {
      // 表格数据
      organizations: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      subMerchants: [],

      // 搜索表单
      searchForm: {
        orgName: '',
        leader: '',
        keyword: ''
      },

      // 选中的行
      selectedIds: [],

      // 对话框
      dialogVisible: false,
      dialogTitle: '新增组织机构',
      isEdit: false,
      form: {
        id: null,
        orgName: '',
        location: '',
        leader: '',
        contact: '',
        orgAddress: '',
        remark: '',
        status: 1,
        subMchId: null
      },
      rules: {
        orgName: [
          { required: true, message: '请输入组织机构名称', trigger: 'blur' },
          { min: 1, max: 30, message: '组织机构名称长度在 1 到 30 个字符', trigger: 'blur' }
        ],
        location: [
          { min: 1, max: 20, message: '所在地长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        leader: [
          { required: true, message: '请输入联系人', trigger: 'blur' },
          { min: 1, max: 20, message: '联系人长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        contact: [
          { required: true, message: '请输入联系方式', trigger: 'blur' },
          { min: 1, max: 20, message: '联系方式长度在 1 到 20 个字符', trigger: 'blur' }
        ],
        orgAddress: [
          { min: 1, max: 40, message: '详细地址长度在 1 到 40 个字符', trigger: 'blur' }
        ],
        remark: [
          { min: 1, max: 200, message: '备注长度在 1 到 200 个字符', trigger: 'blur' }
        ]
      }
    }
  },

  created() {
    this.loadOrganizations()
  },

  methods: {
    // 加载组织机构列表
    loadOrganizations() {
      // 组装请求参数
      const params = {
        orgName: this.searchForm.orgName, // 搜索-组织机构名称
        leader: this.searchForm.leader,   // 搜索-联系人
        keyword: this.searchForm.keyword, // 搜索-关键字
        page: this.currentPage,           // 分页-当前页
        size: this.pageSize               // 分页-每页条数
      };
      orgApi.findAll(params).then(response => {
        if (response.success) {
          this.organizations = response.data.data
          this.total = response.data.total
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载组织机构失败：' + error.message)
      })
    },

    // 加载子商户列表
    loadSubMerchants(currentBindMchId) {
      // 组装请求参数
      const params = {
        unAssigned: true,
        currentBindMchId: currentBindMchId
      };
      subMerchantApi.findAll(params).then(response => {
        if (response.success) {
          this.subMerchants = response.data.data
        } else {
          this.$message.error(response.message)
        }
      }).catch(error => {
        this.$message.error('加载子商户列表失败：' + error.message)
      })
    },

    // 重置搜索
    resetSearch() {
      this.searchForm = {
        orgName: '',
        leader: '',
        keyword: ''
      }
      this.loadOrganizations()
    },

    // 每页条数变化时
    handleSizeChange(val) {
      this.pageSize = val;
      this.loadOrganizations(); // 重新加载
    },

    // 当前页变化时
    handleCurrentChange(val) {
      this.currentPage = val;
      this.loadOrganizations(); // 重新加载
    },

    // 选择变化
    handleSelectionChange(selection) {
      this.selectedIds = selection.map(item => item.id)
    },

    // 打开新增对话框
    openAddDialog() {
      if (!hasPermission('org:edit')) {
        this.$message.error('无新增组织机构权限');
        return;
      }
      this.loadSubMerchants("")
      this.dialogTitle = '新增组织机构'
      this.isEdit = false
      this.form = {
        id: null,
        orgName: '',
        location: '',
        leader: '',
        contact: '',
        orgAddress: '',
        remark: '',
        status: 1,
        subMchId: null
      }
      this.dialogVisible = true
    },

    // 打开编辑对话框
    openEditDialog(row) {
      if (!hasPermission('org:edit')) {
        this.$message.error('无编辑组织机构权限');
        return;
      }
      this.loadSubMerchants(row.subMchId)
      this.dialogTitle = '编辑组织机构'
      this.isEdit = true
      this.form = { ...row }
      this.dialogVisible = true
    },

    // 提交表单
    submitForm() {
      this.$refs.form.validate(valid => {
        if (valid) {
          if (this.isEdit) {
            // 更新组织机构
            orgApi.update(this.form.id, this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '更新成功')
                this.dialogVisible = false
                this.loadOrganizations()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('更新失败：' + error.message)
            })
          } else {
            // 新增组织机构
            orgApi.add(this.form).then(response => {
              if (response.success) {
                this.$message.success(response.message || '新增成功')
                this.dialogVisible = false
                this.loadOrganizations()
              } else {
                this.$message.error(response.message)
              }
            }).catch(error => {
              this.$message.error('新增失败：' + error.message)
            })
          }
        }
      })
    },

    // 删除字典类型
    handleDelete(id) {
      if (!hasPermission('org:edit')) {
        this.$message.error('无删除组织机构权限');
        return;
      }
      this.$confirm('确定要删除该组织机构吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        orgApi.delete(id).then(response => {
          if (response.success) {
            this.$message.success(response.message || '删除成功')
            this.loadOrganizations()
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    // 批量删除字典类型
    handleBatchDelete() {
      if (!hasPermission('org:edit')) {
        this.$message.error('无批量删除组织机构权限');
        return;
      }
      if (this.selectedIds.length === 0) {
        this.$message.warning('请选择要删除的组织机构');
        return;
      }
      this.$confirm('确定要批量删除选中的组织机构吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        orgApi.deleteBatch(this.selectedIds).then(response => {
          if (response.success) {
            this.$message.success(response.message || '批量删除成功')
            this.loadOrganizations()
            this.selectedIds = []
          } else {
            this.$message.error(response.message)
          }
        }).catch(error => {
          this.$message.error('批量删除失败：' + error.message)
        })
      }).catch(() => {
        // 取消操作（此处可留空，仅用于捕获异常）
      });
    },

    hasPermission(permCode) {
      return hasPermission(permCode);
    },
  }
}
</script>

<style scoped></style>